import { emitDismiss, emitToast } from "@/components/providers/toast-provider";
import { ToastConfig } from "@/stores/toast.store";
import { sanitizeToastMessage } from "@/utils/error-message";

type ToastOptions = Partial<Omit<ToastConfig, "id" | "message" | "type">>;

export interface UseToastReturn {
  toasts: ToastConfig[];
  showToast: (toast: Omit<ToastConfig, "id">) => void;
  showSuccess: (message: string, options?: ToastOptions) => void;
  showWarning: (message: string, options?: ToastOptions) => void;
  showError: (message: string, options?: ToastOptions) => void;
  showInfo: (message: string, options?: ToastOptions) => void;
  hideToast: (toastId: string) => void;
  clearAllToasts: () => void;
}

const makeToastId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const showTyped = (
  type: ToastConfig["type"],
  message: string,
  options?: ToastOptions,
  sanitizeOptions?: Parameters<typeof sanitizeToastMessage>[1],
) => {
  emitToast({
    id: makeToastId(),
    type,
    message: sanitizeToastMessage(message, sanitizeOptions),
    duration: 3000,
    position: "middle",
    showCloseButton: true,
    ...options,
  });
};

// Module-level so every `useToast()` call returns the same functions. They hold
// no React state, and stable identities keep them safe in useCallback deps.
const toastApi: UseToastReturn = {
  toasts: [],
  showToast: (toast) => {
    emitToast({
      ...toast,
      id: makeToastId(),
      message: sanitizeToastMessage(toast.message),
      duration: toast.duration ?? 3000,
      position: toast.position ?? "middle",
      showCloseButton: toast.showCloseButton ?? true,
    });
  },
  showSuccess: (message, options) => showTyped("success", message, options),
  showWarning: (message, options) => showTyped("warning", message, options),
  showError: (message, options) => showTyped("error", message, options),
  showInfo: (message, options) =>
    showTyped("info", message, options, { maxLength: Number.POSITIVE_INFINITY }),
  hideToast: (toastId) => emitDismiss(toastId),
  clearAllToasts: () => emitDismiss(),
};

/**
 * Shows toasts through the global ToastProvider. Always use this hook (or its
 * functions) — never write toasts into a store directly, the provider only
 * listens to `emitToast`.
 */
export const useToast = (): UseToastReturn => toastApi;
